import { useDispatch, useSelector } from 'react-redux';
import ButtonPrimaryMedium from '../../../components/common/ButtonPrimary/ButtonPrimaryMedium';
import SearchComponent from '../../../components/common/SearchComponent/SearchComponent';
import { Icon } from '../../../components/icon/icon';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import Tooltip from '../../../components/common/Tooltip/Tooltip';
import ROUTER from '../../../routers/Routers';
import { Pagination, Table } from 'antd';
import ButtonIconOutlineMedium from '../../../components/common/ButtonIconOutline/ButtonIconOutlineMedium';
import { useTranslation } from 'react-i18next';
import { fetchUsersAdmin } from '../../../store/actions/admin.user.action';
import ButtonIconGhostSmall from '../../../components/common/ButtonIconGhost/ButtonIconGhostSmall';

const NewCustomerPage = () => {
  const country_code = process.env.REACT_APP_COUNTRY_CODE ?? 'vn';
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { users } = useSelector(state => state.adminUser);

  //   const saleProducts = useSelector(state => state.saleProduct.saleProducts);

  const { total, nextLink, previousLink } = useMemo(() => {
    return {
      total: users.total,
    };
  }, [users]);

  const [searchParams, setSearchParams] = useSearchParams();
  const offsetParam = searchParams.get('offset') ?? 0;
  const limitParam = searchParams.get('limit') ?? 20;
  const kwParam = searchParams.get('kw');
  const updateSearchParams = useCallback(
    (key, value) => {
      const params = Object.fromEntries([...searchParams]);

      if (value !== null && value !== undefined && value !== '') {
        params[key] = value;
        if (key !== 'offset') {
          params['offset'] = 0;
        }
        setSearchParams({ ...params });
      } else {
        delete params[key];
        setSearchParams(params);
      }
    },
    [searchParams]
  );

  const loadData = useCallback(() => {
    setIsLoading(true);
    dispatch(
      fetchUsersAdmin({
        offset: offsetParam,
        limit: limitParam,
        kw: kwParam,
      })
    ).then(action => setIsLoading(false));
  }, [offsetParam, limitParam, kwParam]);

  // const handleDelete = useCallback(async () => {
  //   setIsLoading(true);
  //   const errorId = [];
  //   await Promise.allSettled(
  //     selectedIds?.forEach(id => {
  //       dispatch(deleteSalteProduct(id)).catch(e => errorId.push(id));
  //     }) ?? []
  //   );
  //   openMessageSuccess({ content: t('Product deleted successfully') });
  //   if (errorId.length) {
  //     openMessageError({
  //       content: `${t('Unable to delete the following codes:')} ${errorId.join(', ')}`,
  //     });
  //   }
  //   setIsLoading(false);
  //   setSelectedIds([]);
  //   setTimeout(() => {
  //     loadData();
  //   }, 500);
  // }, [selectedIds, loadData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadData();
    }, 300);
    return () => clearTimeout(timer);
  }, [offsetParam, limitParam, kwParam]);

  return (
    <div className="flex p-16 items-start gap-16 w-full h-full flex-col">
      <div className="flex justify-between items-center self-stretch">
        <h2 className="text-title-medium-emphasized">{t('Accounts')}</h2>
        <ButtonPrimaryMedium
          title={t('Create account')}
          posIcon={<Icon.ICAddCircle />}
        />
      </div>
      <section className="flex flex-col items-start grow self-stretch rounded-[16px] bg-white">
        <div className="flex p-16 pb-8 items-center gap-12 self-stretch">
          <SearchComponent
            placeholder={t('Search order code, customer...')}
            className="w-[250px]"
            setValue={value => updateSearchParams('kw', value)}
          />
        </div>
        <div className="flex px-16 py-8 flex-col items-start grow self-stretch">
          <RenderTable
            offsetParam={offsetParam}
            dataSource={users.data ?? []}
            isLoading={isLoading}
          />
        </div>

        <div className="flex justify-between items-center self-stretch rounded-[8px] left-0 right-0 bottom-0 p-16 pt-8 bg-white">
          <RenderPagination
            total={total}
            offsetParam={offsetParam}
            limitParam={limitParam}
            updateSearchParams={updateSearchParams}
          />
        </div>
      </section>
    </div>
  );
};

export default NewCustomerPage;

const RenderTable = ({ offsetParam, dataSource, isLoading }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedIds, setSelectedIds] = useState([]);

  const columns = useMemo(() => {
    return [
      {
        title: t('ID'),
        dataIndex: 'id',
        key: 'id',
        width: 250,
        render: value => {
          return (
            <div className="flex px-12 py-8 items-center justify-start">
              <span className="text-body-medium">{value}</span>
            </div>
          );
        },
      },
      {
        title: t("Customer's name"),
        dataIndex: 'fullname',
        key: 'fullname',
        width: 250,
        render: value => {
          return (
            <div className="flex px-12 py-8 items-center justify-start">
              <span className="text-body-medium">{value}</span>
            </div>
          );
        },
      },
      {
        title: t('Email'),
        dataIndex: 'email',
        key: 'email',
        width: 300,
        render: value => {
          return (
            <div className="flex px-12 py-8 items-center justify-start">
              <span className="text-body-medium">{value}</span>
            </div>
          );
        },
      },
      {
        title: t('Phone number'),
        dataIndex: 'phone',
        key: 'phone',
        width: 180,
        render: value => {
          return (
            <div className="flex px-12 py-8 items-center justify-start">
              <span className="text-body-medium">{value}</span>
            </div>
          );
        },
      },
      {
        title: t('Actions'),
        dataIndex: 'actions',
        key: 'actions',
        width: 240,
        render: (_, record) => {
          return (
            <div className="flex px-12 py-8 items-center justify-start">
              <ButtonIconGhostSmall icon={<Icon.ICEdit />} />
              <ButtonIconGhostSmall icon={<Icon.ICDelete />} />
            </div>
          );
        },
      },
    ];
  }, [offsetParam]);

  useEffect(() => {
    setSelectedIds([]);
  }, [dataSource]);

  return (
    <Table
      pagination={false}
      columns={columns.map(item => ({
        ...item,
        // render: (value, record, index) => {
        //   return (
        //     <Tooltip destroyOnHidden={true} title="Double click to edit">
        //       {item.render(value, record, index)}
        //     </Tooltip>
        //   );
        // },
      }))}
      components={{
        body: {
          row: props => {
            return (
              <Tooltip destroyOnHidden={true} title="Double click to edit">
                <tr {...props}>{props?.children}</tr>
              </Tooltip>
            );
          },
        },
      }}
      rowKey="id"
      rowSelection={{
        onChange: keys => {
          setSelectedIds([...keys]);
        },
        selectedRowKeys: selectedIds,
      }}
      onRow={(record, rowIndex) => {
        return {
          onClick: event => {}, // click row
          onDoubleClick: event => {
            navigate(ROUTER.CMS + ROUTER.CUSTOMER + '/' + record?.id);
          }, // double click row
          onContextMenu: event => {}, // right button click row
          onMouseEnter: event => {}, // mouse enter row
          onMouseLeave: event => {}, // mouse leave row
        };
      }}
      dataSource={[...(dataSource ?? [])]}
      loading={isLoading}
      scroll={{ y: 600 }}
    />
  );
};

export const RenderPagination = ({
  offsetParam,
  limitParam,
  total,
  updateSearchParams,
}) => {
  const { t } = useTranslation();

  return (
    <Pagination
      // onShowSizeChange={onShowSizeChange}
      className="custom-pagination flex grow [&_.ant-pagination-item-active_a]:!text-white-high"
      current={offsetParam / (limitParam ?? 10) + 1}
      pageSize={limitParam ?? 10} // items per page
      total={total} // total items
      showSizeChanger={{
        placement: 'bottomRight',
        suffixIcon: <Icon.ICExpandMore />,
        options: [
          ...[5, 10, 20, 30].map(item => ({
            label: (
              <span className="text-body-medium font-semibold !text-black/80">
                {t('Show') + ' ' + item}
              </span>
            ),
            value: item,
          })),
        ],
      }} // allow changing page size
      showTotal={(total, range) => (
        <span className="text-body-medium text-black/60">
          {t('Total') + ' item: ' + total}
        </span>
      )}
      pageSizeOptions={[5, 10, 15, 20]}
      position={['bottomCenter']}
      onChange={(page, pageSize) => {
        // handle page change
        updateSearchParams('offset', (page - 1) * pageSize);
        if (limitParam !== pageSize) updateSearchParams('limit', pageSize);
      }}
      prevIcon={
        <ButtonIconOutlineMedium
          className="rounded-[4px] ms-auto"
          icon={<Icon.ICPrev />}
        />
      }
      nextIcon={
        <ButtonIconOutlineMedium
          className="rounded-[4px]"
          icon={<Icon.ICNext />}
        />
      }
    />
  );
};
